import React, { FC, Fragment, ReactNode, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { CardContent, CardMedia, Grid, Typography, TypographyProps } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';

const Lottie = lazy(() => import('react-lottie'));

interface IHeaderOptions {
  type: 'animation' | 'image' | 'custom';
  asset: object | string | ReactNode;
  width?: number;
  height?: number;
}

interface IContentOptions {
  text: string;
  variant?: TypographyProps['variant'];
  icons?: string;
}

interface IProps extends WithStyles<typeof styles> {
  stepWidth?: number;
  headerOptions: IHeaderOptions;
  contentOptions: IContentOptions;
}

const styles = (theme: Theme) =>
  createStyles({
    imgContainer: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(0.5),
    },
    contentFirst: {
      padding: 0,
    },
    contentLast: {
      '&:last-child': {
        paddingBottom: 0,
      },
    },
  });

const DEFAULT_WIDTH = 320;
const DEFAULT_ASSET_SIZE = 150;

const WalkthroughStep: FC<IProps> = ({ classes, stepWidth, headerOptions, contentOptions }) => {
  const { t } = useTranslation();

  const { asset, height = DEFAULT_ASSET_SIZE, width = DEFAULT_ASSET_SIZE } = headerOptions;
  const containerWidth = stepWidth || DEFAULT_WIDTH;

  function renderImage() {
    return (
      <Grid container justify="center">
        <Grid item>
          <div className={classes.imgContainer}>
            <CardMedia component="img" image={asset as string} style={{ width, height }} />
          </div>
        </Grid>
      </Grid>
    );
  }

  function renderAnimation() {
    return (
      <Grid container justify="center">
        <Grid item xs>
          <Suspense fallback={<div style={{ height, width }} />}>
            <Lottie
              eventListeners={[]}
              options={{ animationData: asset, loop: false }}
              height={height}
              width={width}
            />
          </Suspense>
        </Grid>
      </Grid>
    );
  }

  function renderCustom() {
    return (
      <Grid container justify="center">
        <Grid item xs>
          {asset}
        </Grid>
      </Grid>
    );
  }

  function renderHeader() {
    const { type } = headerOptions;

    switch (type) {
      case 'animation':
        return renderAnimation();
      case 'image':
        return renderImage();
      case 'custom':
        return renderCustom();
    }
  }

  function renderMain() {
    return (
      <Grid container>
        <Grid item>{renderContent()}</Grid>
      </Grid>
    );
  }

  function renderContent() {
    const { text, icons, variant = 'subtitle2' } = contentOptions;

    return (
      <Fragment>
        <Typography gutterBottom variant={variant} align="center">
          {t(text)}
        </Typography>
        {icons && (
          <Typography variant={variant} align="center">
            {t(icons)}
          </Typography>
        )}
      </Fragment>
    );
  }

  return (
    <div style={{ width: containerWidth }}>
      <CardContent className={classes.contentFirst}>{renderHeader()}</CardContent>
      <CardContent className={classes.contentLast}>{renderMain()}</CardContent>
    </div>
  );
};

export default withStyles(styles)(WalkthroughStep);
